import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AlertCircle } from 'lucide-react';
import CarouselPreview from './CarouselPreview';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  imageSize?: 'default' | 'small';
}

interface MediaWithFallbackProps {
  src: string;
  alt: string;
  className?: string;
  size?: 'default' | 'small';
}

interface ParsedContent {
  renderedContent: React.ReactNode[];
}

// Function to parse carousel markup and extract media items or text items
const parseCarousels = (content: string): ParsedContent => {
  const carouselRegex = /<carousel>(.*?)<\/carousel>/gs;
  const mediaItemRegex = /<media-item>!\[([^\]]*)\]\(([^)]+)\)<\/media-item>/g;
  const textItemRegex = /<item>(.*?)<\/item>/gs;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let carouselIndex = 0;

  while ((match = carouselRegex.exec(content)) !== null) {
    // Add content before the carousel
    if (match.index > lastIndex) {
      const beforeContent = content.slice(lastIndex, match.index);
      if (beforeContent.trim()) {
        parts.push(
          <div key={`text-${carouselIndex}-before`} className="carousel-text-content">
            {beforeContent}
          </div>
        );
      }
    }

    const carouselContent = match[1];

    // Try to parse as media carousel first
    const mediaItems: { src: string; alt: string }[] = [];
    let mediaMatch;
    const mediaRegex = new RegExp(mediaItemRegex.source, 'g');
    while ((mediaMatch = mediaRegex.exec(carouselContent)) !== null) {
      mediaItems.push({
        alt: mediaMatch[1],
        src: mediaMatch[2]
      });
    }

    // If we found media items, render as media carousel
    if (mediaItems.length > 0) {
      parts.push(
        <CarouselPreview
          key={`carousel-${carouselIndex}`}
          mediaItems={mediaItems}
          className="mb-3"
        />
      );
    } else {
      // Try to parse as text carousel (reviews)
      const textItems: string[] = [];
      let textMatch;
      const textRegex = new RegExp(textItemRegex.source, 'g');
      while ((textMatch = textRegex.exec(carouselContent)) !== null) {
        textItems.push(textMatch[1].trim());
      }

      // If we found text items, render as review carousel
      if (textItems.length > 0) {
        parts.push(
          <div key={`review-carousel-${carouselIndex}`} className="bg-yellow-50 border border-yellow-200  p-4 mb-3">
            <div className="flex items-center mb-3">
              <div className="text-sm font-medium text-gray-900">
                Customer Reviews ({textItems.length} review{textItems.length !== 1 ? 's' : ''})
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {textItems.map((item, index) => (
                <div key={index} className="bg-white border border-yellow-300  p-3 shadow-sm">
                  <div className="text-sm text-gray-800 whitespace-pre-wrap">
                    {item}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }
    }

    lastIndex = match.index + match[0].length;
    carouselIndex++;
  }

  // Add remaining content after the last carousel
  if (lastIndex < content.length) {
    const remainingContent = content.slice(lastIndex);
    if (remainingContent.trim()) {
      parts.push(
        <div key={`text-${carouselIndex}-after`} className="carousel-text-content">
          {remainingContent}
        </div>
      );
    }
  }

  // If no carousels were found, return the original content wrapped
  if (parts.length === 0) {
    parts.push(
      <div key="original-content" className="carousel-text-content">
        {content}
      </div>
    );
  }

  return { renderedContent: parts };
};

// Helper function to detect if a URL is a video file
const isVideoFile = (url: string): boolean => {
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv'];
  const urlPath = url.toLowerCase().split('?')[0]; // Remove query parameters
  return videoExtensions.some(ext => urlPath.endsWith(ext));
};

const VideoWithFallback: React.FC<MediaWithFallbackProps> = React.memo(({ src, alt, className, size = 'default' }) => {
  const [videoError, setVideoError] = React.useState(false);

  const handleVideoError = React.useCallback(() => {
    setVideoError(true);
  }, []);

  // Adjust dimensions for side-by-side scenarios
  const isInline = className?.includes('inline-block');
  const videoDimensions = React.useMemo(() => {
    if (size === 'small') {
      return isInline
        ? { maxWidth: '150px', maxHeight: '120px', width: 'auto', height: 'auto' }
        : { maxWidth: '200px', maxHeight: '150px', width: 'auto', height: 'auto' };
    } else {
      return isInline
        ? { maxWidth: '200px', maxHeight: '150px', width: 'auto', height: 'auto' }
        : { maxWidth: '400px', maxHeight: '300px', width: 'auto', height: 'auto' };
    }
  }, [size, isInline]);

  if (videoError) {
    return (
      <span className="inline-flex items-center justify-center p-2 bg-gray-100 border border-dashed border-gray-300  text-gray-500 text-sm">
        <AlertCircle className="h-4 w-4 mr-1" />
        <span>Failed to load video: {alt || 'Video'}</span>
      </span>
    );
  }

  return (
    <video
      src={src}
      className={className}
      onError={handleVideoError}
      style={videoDimensions}
      controls
      preload="metadata"
      title={alt}
    >
      <span className="inline-flex items-center justify-center p-2 bg-gray-100 border border-dashed border-gray-300  text-gray-500 text-sm">
        <AlertCircle className="h-4 w-4 mr-1" />
        <span>Your browser does not support the video tag: {alt || 'Video'}</span>
      </span>
    </video>
  );
}, (prevProps, nextProps) => {
  // Only re-render if src, alt, className, or size changes
  return prevProps.src === nextProps.src &&
    prevProps.alt === nextProps.alt &&
    prevProps.className === nextProps.className &&
    prevProps.size === nextProps.size;
});

VideoWithFallback.displayName = 'VideoWithFallback';

const ImageWithFallback: React.FC<MediaWithFallbackProps> = React.memo(({ src, alt, className, size = 'default' }) => {
  const [imageError, setImageError] = React.useState(false);

  const handleImageError = React.useCallback(() => {
    setImageError(true);
  }, []);

  // Adjust dimensions for side-by-side scenarios
  const isInline = className?.includes('inline-block');
  const imageDimensions = React.useMemo(() => {
    if (size === 'small') {
      return isInline
        ? { maxWidth: '150px', maxHeight: '120px', width: 'auto', height: 'auto' }
        : { maxWidth: '200px', maxHeight: '150px', width: 'auto', height: 'auto' };
    } else {
      return isInline
        ? { maxWidth: '200px', maxHeight: '150px', width: 'auto', height: 'auto' }
        : { maxWidth: '400px', maxHeight: '300px', width: 'auto', height: 'auto' };
    }
  }, [size, isInline]);

  if (imageError) {
    return (
      <span className="inline-flex items-center justify-center p-2 bg-gray-100 border border-dashed border-gray-300  text-gray-500 text-sm">
        <AlertCircle className="h-4 w-4 mr-1" />
        <span>Failed to load: {alt || 'Image'}</span>
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={handleImageError}
      style={imageDimensions}
    />
  );
}, (prevProps, nextProps) => {
  // Only re-render if src, alt, className, or size changes
  return prevProps.src === nextProps.src &&
    prevProps.alt === nextProps.alt &&
    prevProps.className === nextProps.className &&
    prevProps.size === nextProps.size;
});

ImageWithFallback.displayName = 'ImageWithFallback';

const MarkdownRenderer: React.FC<MarkdownRendererProps> = React.memo(({ content, className = '', imageSize = 'default' }) => {
  // Function to check if a paragraph contains multiple images (side-by-side scenario)
  const hasMultipleImages = React.useCallback((node: any) => {
    if (!node || !node.children) return false;
    const imageCount = node.children.filter((child: any) => child.type === 'element' && child.tagName === 'img').length;
    return imageCount > 1;
  }, []);

  // Check if content contains carousel markup
  const hasCarousels = content.includes('<carousel>');

  // Memoize the components object to prevent recreation on every render
  const components = React.useMemo(() => ({
    img: ({ node, ...props }: any) => {
      // Check if this image is in a paragraph with multiple images
      const parent = node?.parent;
      const isInMultiImageParagraph = parent && hasMultipleImages(parent);

      const src = props.src || '';
      const alt = props.alt || '';
      const className = ` border border-gray-200 shadow-sm ${isInMultiImageParagraph
        ? 'inline-block mr-2 mb-2'
        : 'block'
        }`;

      // Check if the source is a video file
      if (isVideoFile(src)) {
        return (
          <VideoWithFallback
            src={src}
            alt={alt}
            className={className}
            size={imageSize}
          />
        );
      }

      // Default to image
      return (
        <ImageWithFallback
          src={src}
          alt={alt}
          className={className}
          size={imageSize}
        />
      );
    },
    a: ({ node, ...props }: any) => (
      <a
        {...props}
        className="text-blue-600 hover:text-blue-800 underline"
        target="_blank"
        rel="noopener noreferrer"
      />
    ),
    p: ({ node, ...props }: any) => {
      // Check if this paragraph contains multiple images for side-by-side layout
      const containsMultipleImages = hasMultipleImages(node);

      return (
        <p
          {...props}
          className={`mb-3 text-gray-700 leading-relaxed ${containsMultipleImages ? 'flex flex-wrap items-start gap-2' : ''
            }`}
        />
      );
    },
    h1: ({ node, ...props }: any) => (
      <h1 {...props} className="text-xl font-bold text-gray-900 mb-4" />
    ),
    h2: ({ node, ...props }: any) => (
      <h2 {...props} className="text-lg font-semibold text-gray-900 mb-3" />
    ),
    h3: ({ node, ...props }: any) => (
      <h3 {...props} className="text-base font-medium text-gray-900 mb-2" />
    ),
    ul: ({ node, ...props }: any) => (
      <ul {...props} className="list-disc list-inside mb-3 space-y-1" />
    ),
    ol: ({ node, ...props }: any) => (
      <ol {...props} className="list-decimal list-inside mb-3 space-y-1" />
    ),
    li: ({ node, ...props }: any) => (
      <li {...props} className="text-gray-700" />
    ),
    blockquote: ({ node, ...props }: any) => (
      <blockquote {...props} className="border-l-4 border-blue-200 pl-4 italic text-gray-600 mb-3" />
    ),
    code: ({ node, ...props }: any) => {
      const { inline, ...restProps } = props;
      if (inline) {
        return (
          <code {...restProps} className="bg-gray-100 text-gray-800 px-1 py-0.5  text-sm font-mono" />
        );
      }
      return (
        <code {...restProps} className="block bg-gray-100 text-gray-800 p-3  text-sm font-mono overflow-x-auto" />
      );
    },
    table: ({ node, ...props }: any) => (
      <div className="overflow-x-auto mb-4">
        <table {...props} className="min-w-full divide-y divide-gray-200 border border-gray-200 " />
      </div>
    ),
    th: ({ node, ...props }: any) => (
      <th {...props} className="px-3 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" />
    ),
    td: ({ node, ...props }: any) => (
      <td {...props} className="px-3 py-2 text-sm text-gray-700 border-b border-gray-200" />
    ),
  }), [imageSize, hasMultipleImages]); // Include imageSize and hasMultipleImages in dependency array

  // If content has carousels, parse and render them separately
  if (hasCarousels) {
    const { renderedContent } = parseCarousels(content);

    return (
      <div className={`prose prose-sm max-w-none ${className}`}>
        {renderedContent.map((part, index) => {
          // If it's a carousel preview, render it directly
          if (React.isValidElement(part) && part.type === CarouselPreview) {
            return <div key={index} className="mb-3">{part}</div>;
          }

          // If it's text content, render it with ReactMarkdown
          if (React.isValidElement(part) &&
            part.props &&
            typeof part.props === 'object' &&
            'className' in part.props &&
            part.props.className === 'carousel-text-content') {
            const textContent = (part.props as any).children;
            return (
              <ReactMarkdown
                key={index}
                remarkPlugins={[remarkGfm]}
                components={components}
              >
                {textContent}
              </ReactMarkdown>
            );
          }

          return part;
        })}
      </div>
    );
  }

  // Default rendering for content without carousels
  return (
    <div className={`prose prose-sm max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if content, className, or imageSize changes
  return prevProps.content === nextProps.content &&
    prevProps.className === nextProps.className &&
    prevProps.imageSize === nextProps.imageSize;
});

MarkdownRenderer.displayName = 'MarkdownRenderer';

export default MarkdownRenderer;
